<template>
  <div class="invoice-selector-container">
    <div class="invoice-selector-header">
      <text class="title">發票資訊</text>
    </div>
    
    <!-- 發票類型 Tabs -->
    <div class="invoice-tabs">
      <button 
        v-for="(type, index) in invoiceTypes" 
        :key="index"
        :class="currentTabIndex === index ? 'tab-active' : 'tab'"
        @click="setCurrentTab(index)"
      >
        {{ type }}
      </button>
    </div>
    
    <!-- 手機載具輸入 -->
    <div v-if="currentTabIndex === 1" class="invoice-input-container">
      <text class="input-label">載具號碼：</text>
      <div class="input-wrapper">
        <input 
          class="input-field"
          type="text"
          placeholder="請輸入手機載具"
          v-model="carrierInput"
          maxlength="8"
          @input="handleCarrierInput"
        />
      </div>
    </div>
    
    <!-- 統一編號輸入 -->
    <div v-if="currentTabIndex === 2" class="invoice-input-container">
      <text class="input-label">統一編號：</text>
      <div class="input-wrapper">
        <input 
          class="input-field"
          type="text"
          inputmode="numeric"
          placeholder="請輸入統一編號"
          v-model="vatNumberInput"
          maxlength="8"
        />
      </div>
    </div>
    
    <!-- 愛心碼輸入 -->
    <div v-if="currentTabIndex === 3" class="invoice-input-container">
      <text class="input-label">愛心碼：</text>
      <div class="input-wrapper">
        <input 
          class="input-field"
          type="text"
          inputmode="numeric"
          placeholder="請輸入愛心碼"
          v-model="npoBanInput"
          maxlength="7"
        />
      </div>
    </div>
    
    <!-- 錯誤訊息顯示 -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import validationUtils from '@/utils/validation.utils'

export default {
  name: 'InvoiceSelector',
  
  props: {
    // 初始發票類型索引（0: 紙本發票, 1: 手機載具, 2: 統一編號, 3: 愛心碼）
    initialInvoiceTypeIndex: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0 && value <= 3
    },
    
    // 初始發票號碼（向後相容）
    initialInvoiceNumber: {
      type: String,
      default: null
    },
    
    // 是否顯示確定按鈕
    showConfirmButton: {
      type: Boolean,
      default: true
    },
    
    // 是否同步更新會員資料
    syncToMember: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['invoice-updated', 'validation-error'],
  
  data() {
    return {
      // 發票類型選項
      invoiceTypes: ['紙本發票', '手機載具', '統一編號', '愛心碼'],
      
      // 當前選擇的發票類型索引
      currentTabIndex: this.initialInvoiceTypeIndex,
      
      // 手機載具輸入值
      carrierInput: '',
      
      // 統一編號輸入值
      vatNumberInput: '',
      
      // 愛心碼輸入值
      npoBanInput: '',
      
      // 錯誤訊息
      errorMessage: null,
      
      // AbortController 用於取消先前的請求
      currentController: null
    }
  },
  
  computed: {
    // 從 Vuex store 取得發票類型索引
    storeInvoiceTypeIndex() {
      return this.$store.getters['cart/invoiceTypeIndex']
    },
    
    // 從 Vuex store 取得手機載具
    storeInvoiceCarrier() {
      return this.$store.getters['cart/invoiceCarrier'] ?? null
    },
    
    // 從 Vuex store 取得統一編號
    storeInvoiceVatNumber() {
      return this.$store.getters['cart/invoiceVatNumber'] ?? null
    },
    
    // 從 Vuex store 取得愛心碼
    storeInvoiceNpoBan() {
      return this.$store.getters['cart/invoiceNpoBan'] ?? null
    },
    
    // 從 Vuex store 取得會員資料
    memberInfo() {
      return this.$store.state.userData?.memberInfo ?? null
    }
  },
  
  watch: {
    // 監聽 store 中的發票類型索引變化
    storeInvoiceTypeIndex(newValue) {
      if (newValue !== this.currentTabIndex) {
        this.currentTabIndex = newValue
      }
    },
    
    // 監聽 store 中的手機載具變化
    storeInvoiceCarrier(newValue) {
      if (newValue !== this.carrierInput) {
        this.carrierInput = newValue ?? ''
      }
    },
    
    // 監聽 store 中的統一編號變化
    storeInvoiceVatNumber(newValue) {
      if (newValue !== this.vatNumberInput) {
        this.vatNumberInput = newValue ?? ''
      }
    },
    
    // 監聽 store 中的愛心碼變化
    storeInvoiceNpoBan(newValue) {
      if (newValue !== this.npoBanInput) {
        this.npoBanInput = newValue ?? ''
      }
    }
  },
  
  mounted() {
    // 組件初始化時載入資料
    // 順序很重要：先從 store 載入，再從會員資料載入預設值
    console.log('InvoiceSelector 組件已掛載，開始初始化')
    
    // 1. 從 Vuex store 載入已儲存的發票資料
    this.loadInvoiceDataFromStore()
    
    // 2. 從會員資料載入預設值（只在欄位為空時）
    // 需求 7.4: 組件初始化時從會員資料載入預設值
    this.loadDefaultFromMemberData()
    
    // 3. 智慧預設邏輯：根據手機載具是否有資料決定預設索引
    this.applySmartDefaultIndex()
    
    // 4. 初始化 cart/payment 中的發票資料
    this.initializePaymentInvoiceData()
    
    console.log('InvoiceSelector 初始化完成', {
      currentTabIndex: this.currentTabIndex,
      carrierInput: this.carrierInput,
      vatNumberInput: this.vatNumberInput,
      npoBanInput: this.npoBanInput
    })
  },
  
  beforeUnmount() {
    // 組件銷毀前取消所有待處理的請求
    if (this.currentController) {
      this.currentController.abort()
      console.log('組件銷毀，取消待處理的請求')
    }
  },
  
  methods: {
    /**
     * 設定當前發票類型
     */
    setCurrentTab(index) {
      this.currentTabIndex = index
      this.errorMessage = null
      
      // 如果選擇紙本發票（索引 0），清空所有發票資料
      if (index === 0) {
        this.carrierInput = ''
        this.vatNumberInput = ''
        this.npoBanInput = ''
      }
      
      // 準備發票資料
      const invoiceData = {
        invoiceTypeIndex: index,
        invoiceCarrier: index === 0 ? null : (this.carrierInput || null),
        invoiceVatNumber: index === 0 ? null : (this.vatNumberInput || null),
        invoiceNpoBan: index === 0 ? null : (this.npoBanInput || null)
      }
      
      // 更新 Vuex store 中的發票類型索引
      this.$store.commit('cart/setInvoiceData', invoiceData)
      
      // 同步更新 cart/payment 物件
      const currentPayment = this.$store.state.cart.payment || {}
      this.$store.commit('cart/setPayment', {
        ...currentPayment,
        ...invoiceData
      })
      
      console.log('切換發票類型，已同步更新 cart/payment')
      
      // 發射事件通知父組件
      this.$emit('invoice-updated', this.getInvoiceData())
    },
    
    /**
     * 處理手機載具輸入（自動轉大寫）
     */
    handleCarrierInput(event) {
      this.carrierInput = event.target.value.toUpperCase()
    },
    
    /**
     * 更新發票資料到 Vuex store 和會員資料（不進行驗證）
     * 由 validate() 方法在驗證通過後呼叫
     */
    async updateInvoiceDataWithoutValidation() {
      try {
        // 準備發票資料
        const invoiceData = {
          invoiceTypeIndex: this.currentTabIndex,
          invoiceCarrier: this.currentTabIndex === 0 ? null : (this.carrierInput?.trim() || null),
          invoiceVatNumber: this.currentTabIndex === 0 ? null : (this.vatNumberInput?.trim() || null),
          invoiceNpoBan: this.currentTabIndex === 0 ? null : (this.npoBanInput?.trim() || null)
        }
        
        // 1. 更新 Vuex store 的發票欄位
        this.$store.commit('cart/setInvoiceData', invoiceData)
        
        console.log('Vuex store 已更新發票資料')
        
        // 2. 同步更新 cart/payment 物件
        const currentPayment = this.$store.state.cart.payment || {}
        this.$store.commit('cart/setPayment', {
          ...currentPayment,
          ...invoiceData
        })
        
        console.log('cart/payment 已同步更新發票資料')
        
        // 3. 只有在 syncToMember 為 true 時才同步會員資料
        if (this.syncToMember) {
          await this.syncMemberData()
          console.log('已同步更新會員資料')
        } else {
          console.log('跳過會員資料同步（syncToMember = false）')
        }
        
        console.log('發票資料更新成功')
        
        // 發射事件通知父組件
        this.$emit('invoice-updated', this.getInvoiceData())
        
      } catch (error) {
        console.error('發票資料更新失敗:', error)
        throw error
      }
    },
    
    /**
     * 公開驗證方法，供父組件在提交時呼叫
     * @returns {boolean} 驗證是否通過
     */
    validate() {
      this.errorMessage = null
      
      try {
        // 如果發票功能未啟用，直接通過
        if (!this.$store.getters['basic/enableInvoiceSelector']) {
          return true
        }
        
        // 紙本發票（索引 0）不需要驗證，直接通過
        if (this.currentTabIndex === 0) {
          this.updateInvoiceDataWithoutValidation()
          return true
        }
        
        if (this.currentTabIndex === 1) {
          // 手機載具驗證
          if (this.carrierInput && !validationUtils.isInvoiceCarrierValid(this.carrierInput)) {
            throw new Error('手機載具格式不正確，應為 / 開頭加 7 碼英數字')
          }
        } else if (this.currentTabIndex === 2) {
          // 統一編號驗證
          if (this.vatNumberInput && !validationUtils.isVatNumberValid(this.vatNumberInput)) {
            throw new Error('統一編號驗證失敗，請確認號碼正確')
          }
        } else if (this.currentTabIndex === 3) {
          // 愛心碼驗證
          if (this.npoBanInput && !validationUtils.isNpoBanValid(this.npoBanInput)) {
            throw new Error('愛心碼格式不正確，應為 3-7 位數字')
          }
        }
        
        // 驗證通過，更新資料到 store 和會員資料
        this.updateInvoiceDataWithoutValidation()
        
        return true
      } catch (error) {
        this.errorMessage = error.message
        this.$emit('validation-error', error.message)
        return false
      }
    },
    
    /**
     * 從 Vuex store 載入發票資料
     */
    loadInvoiceDataFromStore() {
      // 只有當 store 中有明確的索引值時才覆蓋 currentTabIndex
      // 如果 store 中是 null，保持 data() 中的預設值（0）
      if (this.storeInvoiceTypeIndex !== null && this.storeInvoiceTypeIndex !== undefined) {
        this.currentTabIndex = this.storeInvoiceTypeIndex
      }
      // 如果 store 中是 null，currentTabIndex 保持預設值 0（紙本發票）
      
      this.carrierInput = this.storeInvoiceCarrier ?? ''
      this.vatNumberInput = this.storeInvoiceVatNumber ?? ''
      this.npoBanInput = this.storeInvoiceNpoBan ?? ''
    },
    
    /**
     * 初始化 cart/payment 中的發票資料
     * 確保即使使用者沒有切換 Tab 或修改輸入，payment 也包含正確的發票類型
     */
    initializePaymentInvoiceData() {
      const currentPayment = this.$store.state.cart.payment || {}
      
      // 準備當前的發票資料
      const invoiceData = {
        invoiceTypeIndex: this.currentTabIndex,
        invoiceCarrier: this.currentTabIndex === 0 ? null : (this.carrierInput?.trim() || null),
        invoiceVatNumber: this.currentTabIndex === 0 ? null : (this.vatNumberInput?.trim() || null),
        invoiceNpoBan: this.currentTabIndex === 0 ? null : (this.npoBanInput?.trim() || null)
      }
      
      // 更新 cart/payment（保留其他欄位）
      this.$store.commit('cart/setPayment', {
        ...currentPayment,
        ...invoiceData
      })
      
      console.log('已初始化 cart/payment 發票資料:', invoiceData)
    },
    
    /**
     * 智慧預設邏輯：根據手機載具是否有資料決定預設索引
     * - 如果手機載具沒有資料 → 預設索引 = 0（紙本發票）
     * - 如果手機載具有資料 → 預設索引 = 1（手機載具）
     * 
     * 重要：只在 store 中沒有明確的發票類型索引時才執行
     */
    applySmartDefaultIndex() {
      // 檢查 store 中是否已有明確的發票類型索引
      // 如果 storeInvoiceTypeIndex 不是 null 或 undefined，表示使用者已經選擇過
      // 包括選擇紙本發票（索引 0）的情況
      const hasExplicitSelection = this.storeInvoiceTypeIndex !== null && this.storeInvoiceTypeIndex !== undefined
      
      if (hasExplicitSelection) {
        console.log('Store 中已有明確的發票類型選擇，保留使用者選擇:', this.storeInvoiceTypeIndex)
        return
      }
      
      // 只有在 store 中沒有資料時，才執行智慧預設邏輯
      console.log('Store 中無發票類型資料，執行智慧預設邏輯')
      
      // 檢查手機載具是否有資料
      const hasCarrierData = this.carrierInput && this.carrierInput.trim() !== ''
      
      // 如果當前索引是 0（紙本發票）且有手機載具資料，則切換到手機載具
      if (this.currentTabIndex === 0 && hasCarrierData) {
        this.currentTabIndex = 1
        console.log('手機載具有資料，自動切換到手機載具（索引 1）', this.carrierInput)
        
        // 更新 store 中的發票類型索引
        const invoiceData = {
          invoiceTypeIndex: this.currentTabIndex,
          invoiceCarrier: this.carrierInput?.trim() || null,
          invoiceVatNumber: this.vatNumberInput?.trim() || null,
          invoiceNpoBan: this.npoBanInput?.trim() || null
        }
        
        this.$store.commit('cart/setInvoiceData', invoiceData)
        console.log('智慧預設邏輯已套用，更新 store:', invoiceData)
      } else {
        console.log('保持當前發票類型索引:', this.currentTabIndex)
      }
    },
    
    /**
     * 從會員資料載入預設值
     * 需求：7.4 - InvoiceSelector 在組件初始化時從會員資料載入預設值
     */
    loadDefaultFromMemberData() {
      // 零信任資料流：安全存取會員資料
      if (!this.memberInfo) return
      
      // 只在欄位為空且 store 中也沒有資料時載入預設值
      // 這樣可以避免覆蓋使用者已經在 store 中設定的資料
      if (!this.carrierInput && !this.storeInvoiceCarrier && this.memberInfo.invoice_carrier) {
        this.carrierInput = this.memberInfo.invoice_carrier
        console.log('從會員資料載入手機載具預設值:', this.carrierInput)
      }
      
      // 使用 s_vat_number（店家自填統編）而非 vat_number
      if (!this.vatNumberInput && !this.storeInvoiceVatNumber && this.memberInfo.s_vat_number) {
        this.vatNumberInput = this.memberInfo.s_vat_number
        console.log('從會員資料載入統一編號預設值:', this.vatNumberInput)
      }
      
      if (!this.npoBanInput && !this.storeInvoiceNpoBan && this.memberInfo.npo_ban) {
        this.npoBanInput = this.memberInfo.npo_ban
        console.log('從會員資料載入愛心碼預設值:', this.npoBanInput)
      }
    },
    
    /**
     * 同步更新會員資料
     * 需求 7.1, 7.2, 7.3: 同步更新會員資料中的發票欄位
     * 實作 AbortController 以處理競態條件和取消先前請求
     */
    async syncMemberData() {
      // 取消先前的請求（防止競態條件）
      if (this.currentController) {
        this.currentController.abort()
        console.log('取消先前的會員資料更新請求')
      }
      
      // 建立新的 AbortController
      this.currentController = new AbortController()
      
      try {
        // 零信任資料流：確保會員資料存在
        if (!this.memberInfo) {
          throw new Error('無法取得會員資料')
        }
        
        // 使用 structuredClone 建立深拷貝，避免直接修改 store 中的資料
        const updatedData = structuredClone(this.memberInfo)
        
        // 根據當前發票類型更新對應欄位
        // 需求 7.1: 更新手機載具時同步更新 invoice_carrier
        if (this.currentTabIndex === 0) {
          // 紙本發票：不更新任何會員發票欄位
          console.log('選擇紙本發票，不更新會員發票資料')
        } else if (this.currentTabIndex === 1) {
          updatedData.invoice_carrier = this.carrierInput?.trim() || null
          console.log('同步更新會員資料 - 手機載具:', updatedData.invoice_carrier)
        } 
        // 需求 7.2: 更新統一編號時同步更新 s_vat_number 和 vat_number
        else if (this.currentTabIndex === 2) {
          updatedData.s_vat_number = this.vatNumberInput?.trim() || null
          updatedData.vat_number = this.vatNumberInput?.trim() || null  // 新增：同時更新 vat_number
          console.log('同步更新會員資料 - 統一編號:', updatedData.s_vat_number, updatedData.vat_number)
        } 
        // 需求 7.3: 更新愛心碼時同步更新 npo_ban
        else if (this.currentTabIndex === 3) {
          updatedData.npo_ban = this.npoBanInput?.trim() || null
          console.log('同步更新會員資料 - 愛心碼:', updatedData.npo_ban)
        }
        
        // 呼叫 Vuex action 更新會員資料
        await this.$store.dispatch('userData/updateMemberInfo', updatedData)
        
        console.log('會員資料同步成功')
        
      } catch (error) {
        // 如果是 AbortError，表示請求被取消，不需要拋出錯誤
        if (error.name === 'AbortError') {
          console.log('會員資料更新請求已取消')
          return
        }
        
        // 其他錯誤需要拋出，讓上層處理
        console.error('會員資料同步失敗:', error)
        throw new Error(error.message || '會員資料更新失敗，請稍後再試')
      } finally {
        // 清理 controller 引用
        this.currentController = null
      }
    },
    
    /**
     * 取得當前發票資料
     * 確保返回的資料格式正確，空字串轉換為 null
     */
    getInvoiceData() {
      return {
        invoiceTypeIndex: this.currentTabIndex,
        invoiceCarrier: this.currentTabIndex === 0 ? null : (this.carrierInput?.trim() || null),
        invoiceVatNumber: this.currentTabIndex === 0 ? null : (this.vatNumberInput?.trim() || null),
        invoiceNpoBan: this.currentTabIndex === 0 ? null : (this.npoBanInput?.trim() || null)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-selector-container {
  width: 100%;
  margin-bottom: 20px;
}

.invoice-selector-header {
  margin-bottom: 10px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
}

.invoice-tabs {
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-bottom: 15px;
}

.tab {
  flex: 1;
  padding: 10px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:not(:last-child) {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #e8e8e8;
  }
}

.tab-active {
  flex: 1;
  padding: 10px;
  background-color: #FFC900;
  border: 1px solid #FFC900;
  border-radius: 4px;
  font-size: 16px;
  color: #000;
  font-weight: 600;
  cursor: pointer;
  
  &:not(:last-child) {
    margin-right: 8px;
  }
}

.invoice-input-container {
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 18px;
}

.input-label {
  flex-shrink: 0;
  margin-right: 10px;
  color: #000;
}

.input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
}

.input-field {
  flex: 1;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  
  &:focus {
    border-color: var(--primary-color, #FFC900);
  }
  
  &::placeholder {
    color: #999;
  }
}

.error-message {
  margin-top: 10px;
  padding: 10px;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 14px;
}

// 小螢幕優化：減少固定空間佔用，讓 flex: 1 有更多空間
@media (max-width: 410px) {
 .invoice-selector-container {
    width: 100%;
    max-width: 100%;
    overflow: hidden; // 防止內容超出
  }
  
  .invoice-input-container {
    width: 100%;
    max-width: 100%;
    min-width: 0; // 允許 flex 子元素縮小到內容以下
  }
  
  .input-label {
    margin-right: 6px;
    flex-shrink: 0; // 標籤不縮小
  }
  
  .input-wrapper {
    flex: 1;
    min-width: 0; // 🔑 關鍵：允許縮小到內容以下
    max-width: 100%;
  }
  
  .input-field {
    width: 100%;
    min-width: 0; // 🔑 關鍵：允許縮小
    padding: 6px 8px;
  }
}
</style>
